/* "GrowDaily on your phone" -- the wallpaper, and the fast ways into Lyn-ID.
 *
 * Two things live on one screen because they are the same wish: the user
 * wants GrowDaily to feel like part of the phone rather than an app they go
 * and find.
 *
 * WHAT ANDROID ACTUALLY ALLOWS, STATED PLAINLY
 * --------------------------------------------
 * A third-party app cannot replace the lock screen's camera button, cannot
 * claim a hardware key, and cannot install a system theme -- those belong to
 * the manufacturer. What it CAN do is three things, and this screen offers
 * exactly those three rather than promising a fourth:
 *
 *   - a quick-settings tile: one swipe and one tap, from a locked screen;
 *   - the assistant role: the power-button gesture, if the user hands it over;
 *   - a launcher shortcut: an icon of its own on the home screen.
 *
 * And the wallpaper, which on Android 12+ is more than a picture: the system
 * derives its accent colours from it, so setting it recolours notifications,
 * settings and quick settings too. That is the honest version of "a theme for
 * the whole phone", and the copy says so rather than implying more.
 *
 * NOTHING HAPPENS WITHOUT A TAP. Every button here goes through the system's
 * own dialog. The wallpaper is the one irreversible action -- Android keeps no
 * copy of what it replaces -- so it says so before, not after.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, Loader2, Smartphone, Sparkles, LayoutGrid, Volume2 } from 'lucide-react';
import { useI18n } from '../i18n';
import { VARIANTS, CHOICES, composition, paint } from '../lib/phoneTheme';
import { capabilities, addQuickTile, requestAssistantRole, applyTheme } from '../lib/nativePhone';

const PREVIEW = { width: 150, height: 320 };
const CHOICE_KEYS = Object.keys(CHOICES);

/** A small phone-shaped canvas drawn with the real composition, so the preview
 *  cannot drift from what actually lands on the phone. */
function Preview({ variant, surface, label }) {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        paint(ctx, composition({ ...PREVIEW, surface, variant }));
    }, [variant, surface]);
    return (
        <figure className="m-0">
            <canvas
                ref={ref}
                width={PREVIEW.width}
                height={PREVIEW.height}
                data-testid={`phone-preview-${surface}`}
                data-variant={variant}
                className="w-full rounded-2xl border border-brand-border shadow-sm"
            />
            <figcaption className="text-[10px] uppercase tracking-widest text-brand-ink3 text-center mt-2">{label}</figcaption>
        </figure>
    );
}

export default function PhoneTheme() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [caps, setCaps] = useState(null);
    const [variant, setVariant] = useState(VARIANTS[0]);
    const [choice, setChoice] = useState('full');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null);
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        capabilities().then((c) => { if (alive.current) setCaps(c); });
        return () => { alive.current = false; };
    }, []);

    const refreshCaps = useCallback(() => {
        capabilities().then((c) => { if (alive.current) setCaps(c); });
    }, []);

    const apply = useCallback(async () => {
        setBusy(true);
        setStatus(null);
        const out = await applyTheme({ choice, variant });
        if (!alive.current) return;
        setBusy(false);
        setStatus(out.ok ? { ok: true }
            : { ok: false, reason: out.reason === 'not_allowed' ? 'not_allowed' : 'failed' });
    }, [choice, variant]);

    const onTile = useCallback(async () => {
        await addQuickTile();
        refreshCaps();
    }, [refreshCaps]);

    const onAssistant = useCallback(async () => {
        await requestAssistantRole();
        refreshCaps();
    }, [refreshCaps]);

    const native = !!caps?.native;
    const canWallpaper = !!caps?.wallpaper;

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="phone-theme-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6">
                <ArrowLeft size={14} /> {t('common.back')}
            </button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('phone.eyebrow')}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-2">{t('phone.title')}</h1>
            <p className="text-sm text-brand-ink3 mb-8">{t('phone.intro')}</p>

            {/* ---------------------------------------------------- wallpaper */}
            <section className="mb-10" data-testid="phone-theme-section">
                <h2 className="font-serif text-xl text-brand-ink mb-1">{t('phone.theme_title')}</h2>
                <p className="text-sm text-brand-ink3 mb-5">{t('phone.theme_sub')}</p>

                <div className="flex gap-2 mb-5">
                    {VARIANTS.map((name) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => setVariant(name)}
                            data-testid={`phone-variant-${name}`}
                            aria-pressed={variant === name}
                            className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${variant === name
                                ? 'bg-brand-green text-white'
                                : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                        >
                            {t(`phone.variant_${name}`)}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-6">
                    <Preview variant={variant} surface="home" label={t('phone.preview_home')} />
                    <Preview variant={variant} surface="lock" label={t('phone.preview_lock')} />
                </div>

                <div className="space-y-2 mb-5">
                    {CHOICE_KEYS.map((key) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setChoice(key)}
                            data-testid={`phone-choice-${key}`}
                            aria-pressed={choice === key}
                            className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border text-left transition-colors ${choice === key
                                ? 'border-brand-green bg-brand-green/10'
                                : 'border-brand-border bg-brand-card hover:bg-brand-bg2'}`}
                        >
                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${choice === key ? 'border-brand-green' : 'border-brand-border'}`}>
                                {choice === key && <span className="w-2.5 h-2.5 rounded-full bg-brand-green" />}
                            </span>
                            <span className="text-sm text-brand-ink">{t(`phone.choice_${key}`)}</span>
                        </button>
                    ))}
                </div>

                <p className="text-[11px] text-brand-ink3 leading-relaxed mb-4">{t('phone.material_note')}</p>

                {native ? (
                    <>
                        <p className="text-[11px] text-brand-ochre mb-3" data-testid="phone-replace-warning">
                            {t('phone.replace_warning')}
                        </p>
                        <button
                            type="button"
                            onClick={apply}
                            disabled={busy || !canWallpaper}
                            data-testid="phone-apply"
                            className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
                        >
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Smartphone size={16} />}
                            {busy ? t('phone.applying') : t('phone.apply')}
                        </button>
                        {!canWallpaper && (
                            <p className="text-xs text-brand-terracotta mt-2" data-testid="phone-not-allowed">{t('phone.not_allowed')}</p>
                        )}
                    </>
                ) : (
                    <p className="text-xs text-brand-ink3 bg-brand-bg2 border border-brand-border rounded-2xl p-3" data-testid="phone-needs-app">
                        {t('phone.needs_app')}
                    </p>
                )}

                {status?.ok && (
                    <p className="text-sm text-brand-green mt-3 inline-flex items-center gap-2" data-testid="phone-applied">
                        <Check size={15} /> {t('phone.applied')}
                    </p>
                )}
                {status && !status.ok && (
                    <p className="text-sm text-brand-terracotta mt-3" data-testid="phone-apply-failed">
                        {status.reason === 'not_allowed' ? t('phone.not_allowed') : t('phone.failed')}
                    </p>
                )}
            </section>

            {/* ------------------------------------------------- fast access */}
            <section data-testid="phone-fast-section">
                <h2 className="font-serif text-xl text-brand-ink mb-1">{t('phone.fast_title')}</h2>
                <p className="text-sm text-brand-ink3 mb-5">{t('phone.fast_sub')}</p>

                <Row
                    icon={LayoutGrid}
                    title={t('phone.tile_title')}
                    sub={caps && !caps.tilePrompt && native ? t('phone.tile_manual') : t('phone.tile_sub')}
                    action={caps?.tilePrompt ? t('phone.tile_add') : ''}
                    onAction={onTile}
                    testid="phone-tile"
                    note={!native ? t('phone.unavailable_here') : ''}
                />
                <Row
                    icon={Sparkles}
                    title={t('phone.assist_title')}
                    sub={t('phone.assist_sub')}
                    action={caps?.isAssistant ? '' : (native ? t('phone.assist_set') : '')}
                    onAction={onAssistant}
                    testid="phone-assist"
                    note={caps?.isAssistant ? t('phone.assist_active') : (!native ? t('phone.unavailable_here') : '')}
                />
                <Row
                    icon={Smartphone}
                    title={t('phone.shortcut_title')}
                    sub={t('phone.shortcut_sub')}
                    testid="phone-shortcut"
                    note={!native ? t('phone.unavailable_here') : ''}
                />
                <Row
                    icon={Volume2}
                    title={t('phone.volume_title')}
                    sub={t('phone.volume_sub')}
                    testid="phone-volume"
                    note={!native ? t('phone.unavailable_here') : ''}
                />
            </section>
        </div>
    );
}

function Row({ icon: Icon, title, sub, action, onAction, testid, note }) {
    return (
        <div className="flex items-center gap-4 bg-brand-card border border-brand-border rounded-2xl p-4 mb-3" data-testid={testid}>
            <div className="w-11 h-11 rounded-2xl bg-brand-green/15 text-brand-green flex items-center justify-center flex-shrink-0">
                <Icon size={20} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-serif text-base text-brand-ink">{title}</div>
                <div className="text-xs text-brand-ink3 mt-0.5 leading-relaxed">{sub}</div>
                {note && <div className="text-[11px] text-brand-ink3 mt-1 italic">{note}</div>}
            </div>
            {action ? (
                <button
                    type="button"
                    onClick={onAction}
                    data-testid={`${testid}-action`}
                    className="flex-shrink-0 bg-brand-green hover:bg-brand-greenDark text-white text-sm px-4 py-2 rounded-full transition-colors"
                >
                    {action}
                </button>
            ) : (
                <ChevronRight size={16} className="text-brand-ink3 flex-shrink-0 opacity-40" />
            )}
        </div>
    );
}
